import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';
import MenuTwoToneIcon from '@mui/icons-material/MenuTwoTone';
import { Box, IconButton, Tooltip, styled, useTheme } from '@mui/material';
import { useContext } from 'react';
import { ACTIONS } from 'src/auth/utils/defineAbilityFor';
import AccountMenu from 'src/components/AccountMenu';
import appConfig from 'src/config/appConfig';
import { Can } from 'src/context/canContext';
import { SidebarContext } from 'src/context/SidebarContext';
import useEffectiveApplicationGroupId from 'src/hooks/useEffectiveApplicationGroupId';
import useIsMobile from 'src/hooks/useIsMobile';
import Help from 'src/layouts/NavBar/components/Help';
import HeaderNotifications from 'src/layouts/NavBar/components/Notifications';
import { FileAPIService } from 'src/services/switch-axios-rest-api/file.service';
import { useSearchJobsQuery } from 'src/services/switch-rest-api/job.service';
import { hasConcreteSelection, toScopedId } from 'src/utils/selection';
import DownloadsTray from 'switch-fe-shared/components/Downloads/DownloadsTray';
import FeatureFlag from 'switch-fe-shared/FeatureFlag';
import { useFeatureFlag } from 'switch-fe-shared/hooks/useFeatureFlag';

import Search from './components/Search';

interface HeaderWrapperProps {
  sidebarWidth: string;
}

/**
 * Application groups for which global search is disabled. Used to keep it off on
 * self-hosted deployments that lack Atlas Search (e.g. TNPA). Add the relevant
 * applicationGroupId(s) here.
 *
 * TODO: This hardcoded blocklist is a stopgap. The feature-flag evaluator
 * (evaluateFeatureFlag in switch-fe-shared/hooks/useFeatureFlag) is allow-list
 * only — it can't express "enabled for everyone EXCEPT X". Expand it to support
 * negation (e.g. `excludeOrgs` / `excludeGroups`) so this can be handled in the
 * `ac_global_search__release` flag config instead of in code.
 */
const GLOBAL_SEARCH_BLOCKED_GROUP_IDS: string[] = ['6719f19a3d59881636fe6916'];

const HeaderWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'sidebarWidth',
})<HeaderWrapperProps>(
  ({ theme, sidebarWidth }) => `
        height: ${theme.header.height};
        color: ${theme.header.textColor};
        padding: ${theme.spacing(0, 2)};
        right: 0;
        z-index: 6;
        background-color: ${theme.header.background};
        position: fixed;
        justify-content: space-between;
        width: 100%;
        border-bottom: 1px solid ${theme.palette.divider};
        transition: left 0.3s ease-in-out;
        @media (min-width: ${
          theme.breakpoints.values[appConfig.breakMobileView]
        }px) {
            left: ${sidebarWidth};
            width: auto;
        }
`
);

const NavBar = () => {
  /* Hooks & Use Effects */
  const { sidebarToggle, toggleSidebar, isCollapsed } =
    useContext(SidebarContext);
  const isMobile = useIsMobile();
  const theme = useTheme();

  const isSwitchBrandingRemoved = useFeatureFlag(
    'ac_remove_switch_branding__ops'
  );

  // Disable global search for blocked application groups (deployments without
  // Atlas Search, e.g. TNPA).
  const effectiveGroupId = useEffectiveApplicationGroupId();
  const fileApiService = new FileAPIService();
  const isGlobalSearchBlocked =
    !!effectiveGroupId &&
    GLOBAL_SEARCH_BLOCKED_GROUP_IDS.includes(effectiveGroupId);

  /* States */
  const sidebarWidth = isMobile
    ? '0px'
    : isCollapsed
      ? '70px'
      : theme.sidebar.width;

  /* API Calls */

  /* Functions & Functional Components */

  return (
    <HeaderWrapper
      display='flex'
      alignItems='center'
      sidebarWidth={sidebarWidth}
      style={{ justifyContent: 'flex-start' }}
    >
      <Box
        display='flex'
        alignItems='center'
        width={'100%'}
        justifyContent={'center'}
      >
        <Box
          component='span'
          sx={{
            display: {
              [appConfig.breakMobileView]: 'none',
              xs: 'inline-block',
            },
          }}
        >
          <Tooltip arrow title='Toggle Menu'>
            <IconButton color='primary' onClick={toggleSidebar}>
              {!sidebarToggle ? (
                <MenuTwoToneIcon
                  fontSize='small'
                  sx={{
                    color:
                      theme.palette.mode === 'dark'
                        ? 'white'
                        : theme.colors.neutral.grey[40],
                  }}
                />
              ) : (
                <CloseTwoToneIcon fontSize='small' />
              )}
            </IconButton>
          </Tooltip>
        </Box>
        {!isGlobalSearchBlocked && (
          <FeatureFlag flag='ac_global_search__release'>
            <Search />
          </FeatureFlag>
        )}
        <Box
          flexGrow={1}
          display='flex'
          component='span'
          flexDirection={'row'}
          flex={1}
        />
        <Can I={ACTIONS.READ} a={'Job'}>
          <DownloadsTray
            applicationGroupId={
              hasConcreteSelection(effectiveGroupId)
                ? toScopedId(effectiveGroupId)
                : undefined
            }
            useSearchJobs={useSearchJobsQuery}
            fetchDownload={(job) => fileApiService.fetchDownload(job)}
          />
        </Can>
        <HeaderNotifications />
        {!isSwitchBrandingRemoved && <Help />}
        <AccountMenu />
      </Box>
    </HeaderWrapper>
  );
};

export default NavBar;
